import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SessionLogoutService } from '../auth/session-logout.service';
import { ApiResponse } from '../models/api-response.model';
import { AppError } from '../models/app-error.model';
import { NotificationService } from '../notifications/notification.service';

const CLIENT_ERROR_TITLE = 'Error de solicitud';
const SERVER_ERROR_TITLE = 'Error del servidor';
const GENERIC_ERROR_TITLE = 'Error';

const UNAUTHORIZED_STATUS = 401;
const SILENT_TOAST_MIN_STATUS = 401;
const SILENT_TOAST_MAX_STATUS = 500;

export const SKIP_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

function shouldShowToast(status: number): boolean {
  return status < SILENT_TOAST_MIN_STATUS || status > SILENT_TOAST_MAX_STATUS;
}

export function isCallerHandledError(error: AppError): boolean {
  return !shouldShowToast(error.statusCode);
}

function isApiResponseError(body: unknown): body is ApiResponse<unknown> {
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as ApiResponse<unknown>).status === false &&
    Array.isArray((body as ApiResponse<unknown>).message) &&
    (body as ApiResponse<unknown>).message.every((message) => typeof message === 'string')
  );
}

function isNetworkError(error: HttpErrorResponse): boolean {
  return error.status === 0 || error.error instanceof ProgressEvent;
}

function statusTitle(status: number): string {
  return status >= 500 ? SERVER_ERROR_TITLE : CLIENT_ERROR_TITLE;
}

function toAppError(error: HttpErrorResponse): AppError {
  if (isNetworkError(error)) {
    return {
      statusCode: error.status,
      title: 'Error de conexión',
      messages: ['No fue posible comunicarse con el servidor.'],
    };
  }

  if (isApiResponseError(error.error)) {
    return {
      statusCode: error.status,
      title: statusTitle(error.status),
      messages: error.error.message,
    };
  }

  return {
    statusCode: error.status,
    title: GENERIC_ERROR_TITLE,
    messages: ['Ocurrió un error inesperado.'],
  };
}

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const notificationService = inject(NotificationService);
  const sessionLogoutService = inject(SessionLogoutService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const appError = toAppError(error);

      if (error.status === UNAUTHORIZED_STATUS && req.headers.has('Authorization')) {
        sessionLogoutService.forceLogout();
      } else if (shouldShowToast(error.status) && !req.context.get(SKIP_ERROR_TOAST)) {
        notificationService.error(appError.messages.join(' '), { title: appError.title });
      }

      return throwError(() => appError);
    }),
  );
};
